import { useCallback, useEffect, useState } from 'react';
import type { CreateExportParams } from './useExportJob';
import { useExportJob } from './useExportJob';
import { downloadArtifactBlob } from './downloadArtifact';
import type { ExportJobRecord } from '../../domains/export/models/ExportRequest';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { EXPORT_JOBS, VALUE } from '../../domains/analytics/constants/MetricEvents';

interface UseManagedExportOptions {
  diagramId: string | undefined;
  selectedShapeIds?: string[];
  prepare?: () => Promise<void>;
}

interface UseManagedExportResult {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  onModalOpenChange: (next: boolean) => void;
  startExport: (params: CreateExportParams) => Promise<void>;
  handleCancel: () => void;
  isSubmitting: boolean;
  isSaving: boolean;
  isDownloading: boolean;
  error: Error | null | undefined;
  job: ExportJobRecord | null | undefined;
}

export function useManagedExport({
  diagramId,
  selectedShapeIds,
  prepare,
}: UseManagedExportOptions): UseManagedExportResult {
  const [isOpen, setIsOpen] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [lastRequestedParams, setLastRequestedParams] = useState<CreateExportParams | null>(null);
  const [lastTrackedTerminalState, setLastTrackedTerminalState] = useState<string | null>(null);

  const {
    createExport,
    downloadArtifact,
    reset,
    job,
    isLoading,
    error,
  } = useExportJob({ diagramId, enabled: isOpen, selectedShapeIds });

  const open = useCallback(() => {
    reset();
    setHasDownloaded(false);
    setIsSaving(false);
    setIsDownloading(false);
    setIsOpen(true);
  }, [reset]);

  const close = useCallback(() => {
    setIsOpen(false);
    setHasDownloaded(false);
    setIsSaving(false);
    setIsDownloading(false);
    reset();
  }, [reset]);

  const startExport = useCallback(
    async (params: CreateExportParams) => {
      reset();
      setHasDownloaded(false);
      setIsSaving(true);
      setLastRequestedParams(params);
      setLastTrackedTerminalState(null);
      try {
        if (prepare) {
          await prepare();
        }
        await createExport(params);
      } finally {
        setIsSaving(false);
      }
    },
    [createExport, prepare, reset],
  );

  const handleDownload = useCallback(
    async (jobId: string) => {
      setIsDownloading(true);
      try {
        const artifact = await downloadArtifact(jobId);
        downloadArtifactBlob(job ?? null, artifact);
        close();
      } finally {
        setIsDownloading(false);
      }
    },
    [close, downloadArtifact, job],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (!job || job.status !== 'completed' || !job.id) return;
    if (hasDownloaded) return;

    setHasDownloaded(true);
    handleDownload(job.id).catch((err) => {
      console.error('Download failed:', err);
      setHasDownloaded(false);
    });
  }, [handleDownload, hasDownloaded, isOpen, job]);

  useEffect(() => {
    if (!isOpen || !job?.id) return;
    if (job.status !== 'completed' && job.status !== 'failed') return;
    const terminalKey = `${job.id}:${job.status}`;
    if (terminalKey === lastTrackedTerminalState) return;

    const format = lastRequestedParams?.format ?? job.format;
    const quality = lastRequestedParams?.quality ?? job.quality;
    const reportAnalyticsProps =
      lastRequestedParams?.reportType != null
        ? {
            reportType: lastRequestedParams.reportType,
            ...(lastRequestedParams.reportVariant
              ? { reportVariant: lastRequestedParams.reportVariant }
              : {}),
            hasProgressSince: Boolean(lastRequestedParams.progressSince?.trim()),
            isReport: true as const,
          }
        : {};

    if (job.status === 'completed') {
      Tracker.track(EXPORT_JOBS.COMPLETED, {
        format,
        quality,
        ...reportAnalyticsProps,
      });
      Tracker.track(VALUE.EXPORT_COMPLETED, {
        format,
        quality,
        ...reportAnalyticsProps,
      });
    } else {
      Tracker.track(EXPORT_JOBS.FAILED, {
        format,
        quality,
        errorCode: job.errorCode,
        errorMessage: job.errorMessage,
        ...reportAnalyticsProps,
      });
    }

    setLastTrackedTerminalState(terminalKey);
  }, [isOpen, job, lastRequestedParams, lastTrackedTerminalState]);

  const handleCancel = useCallback(() => {
    close();
  }, [close]);

  const onModalOpenChange = useCallback(
    (next: boolean) => {
      if (next) {
        open();
      } else {
        close();
      }
    },
    [close, open],
  );

  const isSubmitting = isLoading || isSaving || isDownloading;

  return {
    isOpen,
    open,
    close,
    onModalOpenChange,
    startExport,
    handleCancel,
    isSubmitting,
    isSaving,
    isDownloading,
    error,
    job,
  };
}
